import { useState, useEffect, useCallback } from 'react'
import GenericTable from '../GenericTable'
import { d1Query, d1Insert, d1Update, d1Delete } from '../../services/d1Service'
import type { MenuCategory, StatusMessage } from '../../types'

interface Props { onStatus: (msg: StatusMessage) => void }

export default function MenuCategoriesTab({ onStatus }: Props) {
  const [items, setItems] = useState<MenuCategory[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<Partial<MenuCategory> | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try { setItems(await d1Query<MenuCategory>('SELECT * FROM menu_categories ORDER BY sort_order ASC')) }
    catch (e: any) { onStatus({ type: 'error', text: e.message }) }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { load() }, [load])

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editing) return
    try {
      if ((editing as any).id) await d1Update('menu_categories', (editing as any).id, editing)
      else await d1Insert('menu_categories', editing)
      onStatus({ type: 'success', text: 'Menu category saved!' })
      setEditing(null); load()
    } catch (e: any) { onStatus({ type: 'error', text: e.message }) }
  }

  return (
    <GenericTable
      title="Menu Categories" icon="📂"
      items={items} loading={loading} editingItem={editing}
      columns={[
        { key: 'key_name', label: 'Key Name', width: '130px' },
        { key: 'name_bn', label: 'Name (BN)' },
        { key: 'name_en', label: 'Name (EN)' },
        { key: 'sort_order', label: 'Order', width: '70px' }
      ]}
      formFields={[
        { key: 'key_name', label: 'Key Name (unique)', required: true, disabled: !!(editing as any)?.id },
        { key: 'name_bn', label: 'Name (BN)', required: true },
        { key: 'name_en', label: 'Name (EN)', required: true },
        { key: 'sort_order', label: 'Sort Order', type: 'number' }
      ]}
      onAdd={() => setEditing({ key_name: '', name_bn: '', name_en: '', sort_order: 1 })}
      onEdit={(item) => setEditing({ ...item })}
      onDelete={async (id) => { try { await d1Delete('menu_categories', id); onStatus({ type: 'success', text: 'Deleted!' }); load() } catch (e: any) { onStatus({ type: 'error', text: e.message }) } }}
      onSave={handleSave}
      onCancel={() => setEditing(null)}
      onFieldChange={(key, val) => setEditing(prev => prev ? { ...prev, [key]: val } : prev)}
    />
  )
}
